<template>

  <b-row>
    <b-col cols="12">

      <b-card>
        <b-card-title>
          {{ $t('How to upload') }}
        </b-card-title>
        <b-card-text>
          <div>
            {{
              $t('Please prepare an excel (xls or xlsx) file using the following column and adding one element for each row')
            }}.
          </div>

          <spacer height="30" />
          <div>
            <b-table
              responsive
              :items="items"
              class="mb-0"
            />
          </div>
          <spacer height="10" />

          <div align="right">
            <a
              :href="`${templatesURL}/template_import_veicoli.xlsx`"
              target="_blank"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="warning"
                @disabled="disableAllButtons"
              >
                <feather-icon
                  icon="DownloadIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('Download the template') }}</span>
              </b-button>
            </a>
          </div>
        </b-card-text>
      </b-card>

      <b-card
        border-variant="primary"
      >

        <b-card-text>
          <div class="upload-file">
            <div>
              <div class="mb-8 h6">
                <import-excel :on-success="loadDataInTable" />
              </div>

            </div>
          </div>
        </b-card-text>
      </b-card>

      <b-card
        v-if="downloadProcessed"
        ref="list"
        title=""
        action-refresh
        @refresh="refresh"
      >

        <!-- search input -->
        <div class="custom-search d-flex justify-content-end" />

        <!-- table -->
        <vue-good-table
          :columns="columns"
          :rows="rows"
          :rtl="direction"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"

          :pagination-options="{
            enabled: true,
            perPage:pageLength
          }"
        >

          <!-- Slot: Table Column -->
          <template
            slot="table-column"
            slot-scope="props"
          >

            <span>
              {{ $t(props.column.label) }}
            </span>
          </template>

          <!-- Slot: Table Row -->
          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- Column: First Name -->
            <span
              v-if="props.column.field === 'manufacturer'"
              class="text-nowrap v-align-middle"
            >
              <span>{{ props.row.manufacturer }}</span>
            </span>

            <!-- Column: Last Name -->
            <span
              v-else-if="props.column.field === 'model'"
              class="text-nowrap align-middle"
            >
              <span>{{ props.row.model }}</span>

            </span>

            <!-- Column: Vehicle Id -->
            <span
              v-else-if="props.column.field === 'vehicle_id'"
              class="text-nowrap align-middle"
            >
              <span>{{ props.row.vehicle_id }}</span>

            </span>

          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >

            <div class="d-flex justify-content-between flex-wrap">

              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  {{ $t('message.pagelength') }}
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['3','5','10','15']"
                  class="mx-1"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap">  {{ $t('message.of') }} {{ props.total }} {{
                  $t('message.pageText2')
                }} </span>
              </div>
              <div>

                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>

                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>

        <div align="right">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="success"
            @disabled="disableAllButtons"
            @click="finalize_import"
          >

            <span class="align-middle">{{ $t('Complete the import') }}</span>
          </b-button>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'

import { BCardActions } from '@core/components/b-card-actions'
import {
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BAlert,
  BDropdown,
  BDropdownItem,
  BRow,
  BCol,
  BFormCheckbox,
  BCard,
  BCardTitle,
  BCardText,
  BTable,
  BButton,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import useJwt from '@/auth/jwt/useJwt'
import EquipmentService from '@/basic/equipmentService'
import Ripple from 'vue-ripple-directive'

import ExportExcel from '@/views/components/excel-export/ExportExcel'

import Vue from 'vue'
import i18n from '@/libs/i18n'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import ability from '@/libs/acl/ability'
import VehiclesListComponent from '@/views/table/vue-good-table/VehiclesListComponent'
import GoodTableRowGroup from './GoodTableRowGroup.vue'
import GoodTableColumnSearch from './GoodTableColumnSearch.vue'
import GoodTableAdvanceSearch from './GoodTableAdvanceSearch.vue'
import { codeI18n } from './code'
import GoodTableBasic from './GoodTableBasic.vue'
import GoodTableSsr from './GoodTableSsr.vue'
import { environment } from '@/auth/environment'

const moment = require('moment')

Vue.use(ExportExcel)

export default {
  components: {
    BCardCode,
    BCardActions,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BAlert,
    BDropdown,
    BDropdownItem,
    BButton,
    BRow,
    BCol,
    BFormCheckbox,
    GoodTableBasic,
    GoodTableRowGroup,
    GoodTableColumnSearch,
    GoodTableAdvanceSearch,
    VehiclesListComponent,
    GoodTableSsr,
    BCard,
    BCardTitle,
    BTable,
    BCardText,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      templatesURL: environment.templatesURL,
      items: [
        {
          produttore: 'Fiat',
          modello: 'Ducato',
          targa: 'FW086ZF',
        },
        {
          produttore: 'Lancia',
          modello: 'Ypsilon',
          targa: 'FZ025CX',
        },

      ],
      pageLength: 15,
      codeI18n,
      downloadProcessed: false,
      dir: false,
      editDisabled: !ability.can('manage', 'Basic Equipments'),
      disableAllButtons: false,
      terminated_label: 'Mostra dismessi',
      columns: [
        {
          label: 'Manufacturer',
          field: 'manufacturer',
        },
        {
          label: 'Model',
          field: 'model',
        },

        {
          label: 'Vehicle Id',
          field: 'vehicle_id',
        },

      ],

      /*

 manufacturer: `${_item['NOME']}`.trim(),
            model: `${_item['COGNOME']}`.trim(),
            model_id: `${_item['CF']}`.trim(),
            vehicle_id: `${_item['ID']}`.trim(),
            birth_province: `${_item['PROVINCIA DI NASCITA']}`.trim(),
            birth_place: `${_item['LUOGO DI NASCITA']}`.trim(),
            birthday: `${_item['DATA DI NASCITA']}`.trim(),
            protected_category: `${_item['CATEGORIE PROTETTE']}`.trim(),
            phone: `${_item['TELEFONO']}`.trim(),
            mobile: `${_item['CELLULARE']}`.trim(),
            email: `${_item['EMAIL']}`.trim()
 */

      excel_columns: [
        {
          label: i18n.t('Manufacturer'),
          field: 'manufacturer',
        },
        {
          label: i18n.t('Model'),
          field: 'model',
        },
        {
          label: i18n.t('Vehicle Id'),
          field: 'vehicle_id',
        },

      ],
      rows: [],
      terminated_vehicles: [],
      vehicles: [],
      searchTerm: '',
      tableData: [],

    }
  },

  computed: {
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        Current: 'light-primary',
        Professional: 'light-success',
        Rejected: 'light-danger',
        Resigned: 'light-warning',
        Applied: 'light-info',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  mounted() {
    if (!ability.can('manage', 'Basic Equipments')) {
      this.$router.push({ name: 'not-authorized' })
    }
    this.unsubscribe = store.subscribe((mutation, state) => {
      if (mutation.type === 'selected_company') {
        console.log(`Updating selected_company to ${state.userCompany.id}`)
        this.refresh()
      }
    })

    const company_id = store.state.userCompany.id

    // this.refresh();
    // this.$http.get('/good-table/basic')
    // .then(res => { this.rows = res.data })
  },
  beforeDestroy() {
    this.unsubscribe()
  },
  methods: {

    /**
     * Read excle file data and display in table format
     * @author Madhura Shah
     */
    loadDataInTable({ results, header, meta }) {
      console.log(this.tableData)
      console.log(this.header)

      this.header = header
      this.tableData = this.selected = results
      this.sheetName = meta.sheetName
      const selected = this.tableData.map(_item => ({
        manufacturer: `${_item.PRODUTTORE}`.trim(),
        model: `${_item.MODELLO}`.trim(),
        vehicle_id: `${_item.TARGA}`.trim(),
      }))
      console.log('tabledata', this.tableData)
      console.log('selected', selected)
      this.rows = selected
      this.downloadProcessed = true

      console.log('header', this.header)
    },

    refresh() {
      console.log('refresh')
      // this.$refs["list"].showLoading = true

      const company_id = store.state.userCompany.id
      const vehicleService = new EmployeeService(useJwt)

      vehicleService.vehicles(company_id, {}).then(response => {
        this.vehicles = response.data.data.vehicles
        this.terminated_vehicles = response.data.data.terminated_vehicles

        if (this.show_terminated) {
          this.rows = this.terminated_vehicles
        } else {
          this.rows = this.vehicles
        }
        console.log('rows', this.rows)
        this.$refs.list.showLoading = false
      })
    },

    finalize_import() {
      const company_id = store.state.userCompany.id
      const vehicleService = new EquipmentService(useJwt)

      const data = {
        company_id,
        vehicles: this.rows,
      }
      this.disableAllButtons = true

      vehicleService.import_vehicles(data).then(response => {
        console.log('response', response)

        if (response.data.status === 'success') {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: i18n.t('Vehicles imported'),
              icon: 'AlertIcon',
              variant: 'success',
              text: i18n.t('Vehicles added with success'),
            },
          })
          this.$router.push({ name: 'vehicles' })
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: i18n.t('Vehicles not imported'),
              icon: 'AlertIcon',
              variant: 'danger',
              text: i18n.t(response.data.message),
            },
          })
          this.disableAllButtons = false
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
